import React, { useMemo, useState } from 'react';
import { getNumberMeaning } from '../domains/numerology';
import { getNumerologyReducedColor } from './NumerologicalTextReflection';

interface NumerologyColorLegendProps {
  title?: string;
  compact?: boolean;
}

const BASE_NUMBERS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
const MASTER_NUMBERS = [11, 22, 33, 44];

const NumerologyColorLegend: React.FC<NumerologyColorLegendProps> = ({
  title = 'Numerology Color Legend',
  compact = false
}) => {
  const [expanded, setExpanded] = useState(!compact);

  const entries = useMemo(() => {
    return [...BASE_NUMBERS, ...MASTER_NUMBERS].map((value) => {
      const meaning = getNumberMeaning(value);
      const color = getNumerologyReducedColor(value);
      return { value, meaning, color };
    });
  }, []);

  return (
    <div
      style={{
        background: 'rgba(15, 23, 42, 0.78)',
        border: '1px solid rgba(148, 163, 184, 0.35)',
        borderRadius: '12px',
        padding: compact ? '0.75rem' : '1rem'
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        style={{
          width: '100%',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: '#cbd5e1',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: compact ? '0.78rem' : '0.85rem',
          fontWeight: 700,
          letterSpacing: '0.03em',
          padding: 0,
          textTransform: 'uppercase'
        }}
      >
        <span>{title}</span>
        <span>{expanded ? 'Hide' : 'Show'}</span>
      </button>

      {expanded && (
        <div style={{ marginTop: '0.75rem' }}>
          <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: '0.65rem', lineHeight: 1.5 }}>
            Letter coloring uses Pythagorean values reduced to 0-9. Subsection chips show reduced totals. Gradient blends in archetypal cards are equilibrium palettes derived from the analyzed text.
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: compact ? 'repeat(auto-fit, minmax(170px, 1fr))' : 'repeat(auto-fit, minmax(190px, 1fr))',
              gap: '0.5rem'
            }}
          >
            {entries.map((entry) => (
              <div
                key={entry.value}
                style={{
                  border: `1px solid ${entry.color}66`,
                  background: 'rgba(2, 6, 23, 0.65)',
                  borderRadius: '8px',
                  padding: '0.5rem'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                  <span
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      background: entry.color,
                      boxShadow: `0 0 8px ${entry.color}`
                    }}
                  />
                  <span style={{ color: entry.color, fontWeight: 700, fontSize: '0.78rem', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                    {entry.value}
                  </span>
                  <span style={{ color: '#cbd5e1', fontWeight: 600, fontSize: '0.74rem' }}>
                    {entry.meaning.title}
                  </span>
                </div>
                <div style={{ color: '#94a3b8', fontSize: '0.68rem', lineHeight: 1.4 }}>
                  {entry.meaning.essence}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NumerologyColorLegend;